export const sections = [
    {
        id: 'core',
        icon: '🍃',
        color: '#6DB33F',
        bg: 'rgba(109, 179, 63, 0.15)',
        title: 'Spring Boot Core',
        tagline: 'Auto-configuration & Starters',
        simple: 'Spring Boot is all about making Spring easier. It uses "Starters" to pull in dependencies and "Auto-configuration" to set everything up based on what it finds on your classpath. No more complex XML or manual bean definitions.',
        keyPoints: [
            '@SpringBootApplication: Combines @Configuration, @EnableAutoConfiguration, and @ComponentScan.',
            'Starters: Dependency descriptors (e.g., spring-boot-starter-web) that aggregate common libraries.',
            'Auto-configuration: Spring Boot attempts to automatically configure your Spring application based on the jar dependencies.',
            'Embedded Servers: Tomcat, Jetty, or Undertow are embedded directly into the JAR.',
        ],
        code: `@SpringBootApplication
public class MyBackendApplication {
    public static void main(String[] args) {
        SpringApplication.run(MyBackendApplication.class, args);
    }
}

// In pom.xml
<dependency>
    <groupId>org.springframework.boot</groupId>
    <artifactId>spring-boot-starter-data-jpa</artifactId>
</dependency>`,
        qa: [
            {
                q: 'Q: What is the purpose of @SpringBootApplication?',
                a: 'It is a convenience annotation that adds all of the following: **@Configuration**, **@EnableAutoConfiguration**, and **@ComponentScan**. It defines the main entry point and tells Spring where to look for your components.'
            },
            {
                q: 'Q: How does Spring Boot auto-configuration work?',
                a: 'It looks at the libraries on your classpath. If it sees `h2.jar`, it auto-configures an in-memory database. If it sees `spring-webmvc.jar`, it sets up a `DispatcherServlet`. It uses **@ConditionalOnClass** and **@ConditionalOnMissingBean** annotations internally.'
            }
        ]
    },
    {
        id: 'beans',
        icon: '🧱',
        color: '#6DB33F',
        bg: 'rgba(109, 179, 63, 0.15)',
        title: 'Beans & Dependency Injection',
        tagline: 'The IoC Container',
        simple: 'Spring is a "Inversion of Control" (IoC) container. You don\'t create objects using "new"; you let Spring create and manage them (Beans) and "inject" them where they are needed.',
        keyPoints: [
            '@Component: Marks a class as a Spring Bean.',
            '@Service, @Repository, @Controller: Specialized versions of @Component.',
            '@Autowired: Injects a dependency (but constructor injection is preferred).',
            'Scopes: Singleton (default), Prototype, Request, Session.',
        ],
        code: `@Service
public class OrderService {
    private final OrderRepository repository;

    // Constructor Injection (Best Practice)
    public OrderService(OrderRepository repository) {
        this.repository = repository;
    }
}

@Configuration
public class MyConfig {
    @Bean
    public MyExternalLibrary myBean() {
        return new MyExternalLibrary();
    }
}`,
        qa: [
            {
                q: 'Q: Why is constructor injection better than @Autowired on fields?',
                a: '1. **Testability**: You can easily pass mocks to the constructor in unit tests.\n2. **Immutability**: You can make fields `final`.\n3. **Fails Fast**: Circular dependencies are caught at startup.'
            },
            {
                q: 'Q: What are Bean Scopes?',
                a: 'The most common are **Singleton** (one instance per container) and **Prototype** (new instance every time). Web apps also use **Request** and **Session** scopes.'
            }
        ]
    },
    {
        id: 'web',
        icon: '🌐',
        color: '#6DB33F',
        bg: 'rgba(109, 179, 63, 0.15)',
        title: 'Spring Web (REST)',
        tagline: 'Building APIs',
        simple: 'Spring Web makes it easy to build RESTful APIs. You use controllers to map URLs to Java methods and automatically convert JSON to Java objects.',
        keyPoints: [
            '@RestController: Combines @Controller and @ResponseBody.',
            '@RequestMapping: Maps URLs to methods.',
            '@GetMapping, @PostMapping: Shortcut for specific HTTP methods.',
            '@RequestBody: Maps the JSON request body to a Java object.',
        ],
        code: `@RestController
@RequestMapping("/api/orders")
public class OrderController {
    
    @PostMapping
    public ResponseEntity<Order> create(@RequestBody OrderRequest req) {
        Order saved = service.save(req);
        return ResponseEntity.status(HttpStatus.CREATED).body(saved);
    }
    
    @GetMapping("/{id}")
    public Order get(@PathVariable Long id) {
        return service.findById(id);
    }
}`,
        qa: [
            {
                q: 'Q: What is the difference between @Controller and @RestController?',
                a: '@Controller is for traditional web apps (returns a view name). **@RestController** is for REST APIs; every method automatically returns its result as JSON/XML in the response body.'
            }
        ]
    },
    {
        id: 'data',
        icon: '💾',
        color: '#6DB33F',
        bg: 'rgba(109, 179, 63, 0.15)',
        title: 'Spring Data JPA',
        tagline: 'Database Access Made Easy',
        simple: 'Spring Data JPA lets you talk to databases by just defining interfaces. It handles all the boilerplate SQL and mapping for you.',
        keyPoints: [
            'JpaRepository: Provides CRUD operations out of the box.',
            'Query Methods: `findByEmail(String email)` is automatically implemented!',
            '@Transactional: Ensures your method runs in a database transaction.',
            '@Entity: Maps a Java class to a database table.',
        ],
        code: `@Entity
public class User {
    @Id @GeneratedValue
    private Long id;
    private String email;
}

public interface UserRepository extends JpaRepository<User, Long> {
    Optional<User> findByEmail(String email);
}

@Service
@Transactional
public void upgradeUser(Long id) {
    // Everything in this method is 1 transaction
}`,
        qa: [
            {
                q: 'Q: How do Query Methods work?',
                a: 'Spring parses the method name (e.g., `findByNameAndStatus`) and generates the JPQL/SQL query automatically at runtime.'
            },
            {
                q: 'Q: What does @Transactional do?',
                a: 'It wraps your method in a database transaction. If an exception occurs, the transaction is **rolled back**. If it finishes normally, it\'s **committed**.'
            }
        ]
    },
    {
        id: 'security',
        icon: '🔒',
        color: '#6DB33F',
        bg: 'rgba(109, 179, 63, 0.15)',
        title: 'Spring Security',
        tagline: 'Auth & Protection',
        simple: 'Spring Security is a powerful framework for securing your app. It handles everything from login forms to JWT tokens and protection against CSRF/CORS issues.',
        keyPoints: [
            'Security Filter Chain: A series of filters that every request passes through.',
            'Authentication: "Who are you?" (Username/Password, JWT).',
            'Authorization: "What can you do?" (Roles like ADMIN, USER).',
            'SecurityContextHolder: Where Spring stores information about the current user.',
        ],
        code: `@Configuration
@EnableWebSecurity
public class SecurityConfig {
    @Bean
    public SecurityFilterChain filterChain(HttpSecurity http) {
        http.authorizeHttpRequests(auth -> auth
            .requestMatchers("/api/admin/**").hasRole("ADMIN")
            .anyRequest().authenticated()
        ).httpBasic(Customizer.withDefaults());
        return http.build();
    }
}`,
        qa: [
            {
                q: 'Q: What is the Filter Chain?',
                a: 'It is the core of Spring Security. Requests are intercepted by filters like **BasicAuthenticationFilter** or **JwtAuthenticationFilter** before they reach your controller.'
            }
        ]
    },
    {
        id: 'testing',
        icon: '🧪',
        color: '#6DB33F',
        bg: 'rgba(109, 179, 63, 0.15)',
        title: 'Spring Boot Testing',
        tagline: 'Reliability built-in',
        simple: 'Spring Boot provide excellent support for testing at all levels, from unit tests with Mockito to full integration tests using @SpringBootTest.',
        keyPoints: [
            '@SpringBootTest: Loads the full application context.',
            'MockMvc: Allows testing controllers without starting the server.',
            '@MockBean: Injects a Mockito mock into the Spring Context.',
            '@DataJpaTest: For testing the persistence layer with an H2 database.',
        ],
        code: `@SpringBootTest
@AutoConfigureMockMvc
class OrderControllerTest {
    @Autowired private MockMvc mockMvc;
    @MockBean private OrderService service;

    @Test
    void testGetOrder() throws Exception {
        when(service.findById(1L)).thenReturn(new Order(1L));
        mockMvc.perform(get("/api/orders/1"))
               .andExpect(status().isOk())
               .andExpect(jsonPath("$.id").value(1));
    }
}`,
        qa: [
            {
                q: 'Q: What is the difference between @Mock and @MockBean?',
                a: '@Mock is a plain Mockito mock. **@MockBean** is a Spring Boot annotation that adds the mock to the Spring ApplicationContext, replacing any existing bean of that type.'
            },
            {
                q: 'Q: How do you test only the web layer?',
                a: 'Use **@WebMvcTest(MyController.class)**. This loads ONLY the web-related beans (Controllers, Filters) and not the service or repository layers, making the test much faster.'
            }
        ]
    },
    {
        id: 'monitoring',
        icon: '📈',
        color: '#6DB33F',
        bg: 'rgba(109, 179, 63, 0.15)',
        title: 'Monitoring & Actuator',
        tagline: 'Production Readiness',
        simple: 'Spring Boot Actuator adds several production-ready features to your application, like health checks, metrics, and environment info, exposed over HTTP or JMX.',
        keyPoints: [
            '/actuator/health: Shows the health of the app and its dependencies.',
            '/actuator/metrics: Detailed performance data (JVM, CPU, HTTP requests).',
            'Micrometer: The underlying library that integrates with Prometheus/Grafana.',
            'Custom Health Indicators: You can write your own health checks.',
        ],
        code: `// application.yml
management:
  endpoints:
    web:
      exposure:
        include: "health,info,metrics"
  endpoint:
    health:
      show-details: always

// Custom Health Check
@Component
public class MyServiceHealth implements HealthIndicator {
    @Override
    public Health health() {
        if (checkExternalSystem()) return Health.up().build();
        return Health.down().withDetail("Error", "External system unreachable").build();
    }
}`,
        qa: [
            {
                q: 'Q: What are the key benefits of Actuator?',
                a: 'It provides **Observability**. You can monitor if the database is down, check memory usage, or see the latest log entries without having to SSH into the server.'
            }
        ]
    }
];
